import { PROPERTY_APPROVAL_OPPERATIONS } from "../utils/onboarding-helper";

const yup = require("yup");

const propertyContactDraftSchema = yup.object({
  first_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Contact First Name"),
  last_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Contact Last Name"),
  phone_number: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^\d{10}$/, "Must be a 10-digit number")
    .label("Property Contact Phone Number"),
  email: yup
    .string()
    .email()
    .label("Property Contact Email")
    .lowercase("Please Enter Lowercase Letters")
    .strict(),
  customer_id: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Customer"),
});

const propertyContactBaseSchema = yup.object({
  first_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Contact First Name")
    .required(),
  last_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Contact Last Name")
    .required(),
  phone_number: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^\d{10}$/, "Must be a 10-digit number")
    .label("Property Contact Phone Number"),
  email: yup
    .string()
    .email()
    .label("Property Contact Email")
    .lowercase("Please Enter Lowercase Letters")
    .required()
    .strict(),
  customer_id: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Customer"),
});

const propertyContactSchema = yup.object().shape({
  ...propertyContactBaseSchema.fields,
});

const propertyContactAddSchema = yup.object().shape({
  ...propertyContactBaseSchema.fields,
  property_id: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Contact")
    .required(),
});

const propertyContactEditSchema = yup.object().shape({
  ...propertyContactBaseSchema.fields,
  _id: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Contact")
    .required(),
});

const propertyDraftSchema = yup.object({
  property_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Name")
    .required(),
  property_code: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Code"),
  brand: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Brand"),
  pos: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("POS"),
  pms: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("PMS")
    .required(),
  property_time_zone: yup.string().label("Time Zone").required(),
  property_contact: yup.object().shape({
    ...propertyContactDraftSchema.fields,
  }),
});

const addPropertyDraftSchema = yup.object().shape({
  propertyData: yup
    .array()
    .of(
      yup.object().shape({
        ...propertyDraftSchema.fields,
      }),
    )
    .test(
      "exceed-maximum-property-count",
      "Exceeded maximum no of properties allowed",
      (value, testContext) => {
        const { parent } = testContext;
        return value?.length <= parent?.allowedNumberOfProperties;
      },
    )
    .test("unique-property-name", "Property Names must be unique", (value) => {
      const propertyNames = value
        .filter((property) => property.property_name !== "")
        .map((property) => property.property_name);
      return new Set(propertyNames).size === propertyNames.length;
    })
    .test("unique-property-codes", "Property Codes must be unique", (value) => {
      const propertyCodes = value
        .filter((property) => property.property_code !== "")
        .map((property) => property.property_code);

      return new Set(propertyCodes).size === propertyCodes.length;
    }),
});

const onboardingPropertyDraftSchema = yup.object().shape({
  ...propertyDraftSchema.fields,
  property_contact: yup.object().shape({
    ...propertyContactDraftSchema.fields,
    email: yup
      .string()
      .email()
      .label("Property Contact Email")
      .lowercase("Please Enter Lowercase Letters")
      .strict()
      .test(
        "is-unique-contact-email",
        "Duplicate Property Contact Email",
        (value, testContext) => {
          return (
            value?.length === 0 ||
            testContext.from[2].value.propertyData.filter(
              (property) => property.property_contact.email === value,
            ).length === 1
          );
        },
      ),
  }),
  property_name: yup
    .string()
    .label("PropertyName")
    .transform((value) => (value ? value.trim() : value))
    .required()
    .test(
      "is-unique-property-name",
      "Duplicate Property Name",
      (value, testContext) => {
        return (
          value.trim().length === 0 ||
          testContext.from[1].value.propertyData.filter(
            (property) => property.property_name === value,
          ).length === 1
        );
      },
    ),
  property_code: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Code")
    .when("operationType", {
      is: PROPERTY_APPROVAL_OPPERATIONS.APPROVE,
      then: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .required(),
      otherwise: yup
        .string()
        .transform((value) => (value ? value.trim() : value)),
    })
    .test(
      "is-unique-property-code",
      "Duplicate Property Code",
      (value, testContext) => {
        return (
          value.trim().length === 0 ||
          testContext.from[1].value.propertyData.filter(
            (property) => property.property_code === value,
          ).length === 1
        );
      },
    ),
});

const onboardingPropertyApproveValidateSchema = yup.object().shape({
  propertyData: yup
    .array()
    .of(
      yup.object().shape({
        ...propertyDraftSchema.fields,
        property_contact: yup.object().shape({
          ...propertyContactDraftSchema.fields,
          first_name: yup
            .string()
            .transform((value) => (value ? value.trim() : value))
            .label("Property Contact First Name")
            .required(),
          last_name: yup
            .string()
            .transform((value) => (value ? value.trim() : value))
            .label("Property Contact Last Name")
            .required(),
          email: yup
            .string()
            .transform((value) => (value ? value.trim() : value))
            .email()
            .label("Property Contact Email")
            .lowercase("Please Enter Lowercase Letters")
            .required()
            .strict()
            .test(
              "is-unique-contact-email",
              "Duplicate Property Contact Email",
              (value, testContext) => {
                return (
                  value?.trim().length === 0 ||
                  testContext.from[2].value.propertyData.filter(
                    (property) => property.property_contact.email === value,
                  ).length === 1
                );
              },
            ),
        }),
        property_name: yup
          .string()
          .transform((value) => (value ? value.trim() : value))
          .label("Property Name")
          .required()
          .test(
            "is-unique-property-name",
            "Duplicate Property Name",
            (value, testContext) => {
              return (
                value.trim().length === 0 ||
                testContext.from[1].value.propertyData.filter(
                  (property) => property.property_name === value,
                ).length === 1
              );
            },
          ),
        property_code: yup
          .string()
          .transform((value) => (value ? value.trim() : value))
          .label("Property Code")
          .required()
          .test(
            "is-unique-property-code",
            "Duplicate Property Code",
            (value, testContext) => {
              return (
                value.trim().length === 0 ||
                testContext.from[1].value.propertyData.filter(
                  (property) => property.property_code === value,
                ).length === 1
              );
            },
          ),
      }),
    )
    .test(
      "exceed-maximum-property-count",
      "Exceeded maximum no of properties allowed",
      (value, testContext) => {
        const { maxOnboardingProperties } = testContext.options.context;
        console.log(value.length, maxOnboardingProperties);
        return value.length <= maxOnboardingProperties;
      },
    ),
});

const onboardingPropertyEditSchema = yup.object({
  _id: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property"),
  property_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Name")
    .required(),
  property_code: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Property Code")
    .required(),
  pms: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("PMS")
    .required(),
  pos: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("POS"),
  brand: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Brand"),
  property_contact: yup.object({
    first_name: yup
      .string()
      .transform((value) => (value ? value.trim() : value))
      .label("Property Contact First Name")
      .required(),
    last_name: yup
      .string()
      .transform((value) => (value ? value.trim() : value))
      .label("Property Contact Last Name")
      .required(),
    email: yup
      .string()
      .email()
      .label("Property Contact Email")
      .lowercase("Please Enter Lowercase Letters")
      .strict(),
    customer_id: yup
      .string()
      .transform((value) => (value ? value.trim() : value))
      .label("Customer"),
  }),
  customer_id: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Customer"),
});

export {
  onboardingPropertyDraftSchema,
  propertyContactSchema,
  onboardingPropertyApproveValidateSchema,
  onboardingPropertyEditSchema,
  propertyContactAddSchema,
  propertyContactEditSchema,
  propertyDraftSchema,
  addPropertyDraftSchema,
};
